import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText, AlertTriangle, Info, Megaphone, BookOpen, Loader2, ExternalLink } from 'lucide-react';

export default function GovernmentPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [feeds, setFeeds] = useState([]);

  useEffect(() => {
    loadFeeds();
  }, []);

  const loadFeeds = async () => {
    try {
      const response = await api.get('/government/feeds');
      setFeeds(response.data || []);
    } catch (error) {
      console.error('Error loading feeds:', error);
      setFeeds([]);
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'scheme':
        return <FileText className="w-5 h-5 text-green-500" />;
      case 'weather_alert':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'pest_alert':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'market_update':
        return <Megaphone className="w-5 h-5 text-blue-500" />;
      case 'training':
        return <BookOpen className="w-5 h-5 text-purple-500" />;
      default:
        return <Info className="w-5 h-5 text-gray-500" />;
    }
  };

  const getTypeBadge = (type) => {
    const badges = {
      scheme: 'bg-green-100 text-green-700',
      weather_alert: 'bg-yellow-100 text-yellow-700',
      pest_alert: 'bg-red-100 text-red-700',
      market_update: 'bg-blue-100 text-blue-700',
      training: 'bg-purple-100 text-purple-700',
    };
    return badges[type] || 'bg-gray-100 text-gray-700';
  };

  const typeLabel = (type) => t(`government.types.${type || 'update'}`, t('government.types.update'));

  const renderFeeds = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-farm-primary" />
        </div>
      );
    }
    if (feeds.length === 0) {
      return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FileText className="w-12 h-12 text-gray-300 mb-3" />
            <p className="text-muted-foreground">{t('government.empty')}</p>
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="space-y-4">
        {feeds.map((feed) => (
          <Card key={feed.id} className="overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  {getTypeIcon(feed.feed_type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getTypeBadge(feed.feed_type)}`}>
                      {typeLabel(feed.feed_type)}
                    </span>
                    {feed.region && (
                      <span className="text-xs text-muted-foreground">{feed.region}</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{feed.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">{feed.content}</p>
                  <div className="flex items-center justify-between mt-3">
                    <p className="text-xs text-muted-foreground">{feed.source}</p>
                    {feed.source_url && (
                      <a
                        href={feed.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-farm-primary text-sm font-medium h-8"
                        data-testid={`gov-readmore-${feed.id}`}
                      >
                        {feed.is_source_card
                          ? t('government.visitPortal', { defaultValue: 'Visit portal' })
                          : t('common.readMore')}
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div className="p-4 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold">{t('government.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('government.subtitle')}</p>
        </div>
      </div>

      {/* Feeds */}
      {renderFeeds()}
    </div>
  );
}
